import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Lock, AtSign, Eye, EyeOff, Loader2 } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import './Login.css'

const Login = () => {
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const { login } = useAuth()
    const navigate = useNavigate()
    const location = useLocation()
    const from = location.state?.from?.pathname || '/'

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError('')
        setLoading(true)

        try {
            await login(username, password)
            navigate(from, { replace: true })
        } catch (err) {
            setError(err.response?.data?.message || 'Invalid username or password')
        } finally {
            setLoading(false)
        }
    }

    const names = ['DR.Doaa', 'Zeyad', 'Karim', 'Faris', 'Ahmed'];
    const [currentNameIndex, setCurrentNameIndex] = useState(0);
    const [currentText, setCurrentText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        const typeSpeed = 150;
        const deleteSpeed = 100;
        const pauseDelay = 2000;

        const handleTyping = () => {
            const currentName = names[currentNameIndex];

            if (isDeleting) {
                setCurrentText(prev => prev.slice(0, -1));
                if (currentText === '') {
                    setIsDeleting(false);
                    setCurrentNameIndex((prevIndex) => (prevIndex + 1) % names.length);
                }
            } else {
                setCurrentText(currentName.slice(0, currentText.length + 1));
                if (currentText === currentName) {
                    setTimeout(() => setIsDeleting(true), pauseDelay);
                    return; // Pause before deleting
                }
            }
        };

        const timer = setTimeout(handleTyping, isDeleting ? deleteSpeed : typeSpeed);
        return () => clearTimeout(timer);
    }, [currentText, isDeleting, currentNameIndex]);

    return (
        <div className="login-container">
            <div className="login-side-visual">
                <div className="typing-container">
                    <img src="./white-logo.png" alt="IN Logo" className="visual-logo" />
                    <h1 className="welcome-text">Hello 👋🏻</h1>
                    <div className="dynamic-text-wrapper">
                        <span className="dynamic-name">{currentText}</span><span className="cursor">|</span>
                    </div>
                    <p className="visual-subtitle">
                        Monitor listings, manage hangout categories, and shape the experience users discover every day.
                    </p>
                </div>
                <footer className="visual-footer">
                    <p>© 2026 IN-Organization. All rights reserved.</p>
                </footer>
            </div>

            <div className="login-side-form">
                <div className="login-card-minimal">
                    <div className="login-header">
                        <h2>Welcome back</h2>
                        <p>Sign in to your account to continue</p>
                    </div>

                    <form onSubmit={handleSubmit} className="login-form">
                        {error && <div className="error-message">{error}</div>}

                        <div className="form-group">
                            <label htmlFor="username">Username</label>
                            <div className="input-with-icon">
                                <AtSign size={16} />
                                <input
                                    id="username"
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    placeholder="Enter username"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">Password</label>
                            <div className="input-with-icon">
                                <Lock size={16} />
                                <input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="8+ characters required"
                                    required
                                />
                                <button
                                    type="button"
                                    className="show-password"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <button type="submit" className="login-button" disabled={loading}>
                            {loading ? (
                                <>
                                    <Loader2 className="animate-spin" size={20} />
                                    <span>Signing in...</span>
                                </>
                            ) : (
                                'Sign In'
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default Login
